import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
} from "@mui/material";
import { subscriptionService, planService } from "../../../api";
import { useAlert } from "../../../context/AlertContext";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";

const RegionalAnalytics = () => {
  const [filters, setFilters] = useState({
    country: "",
    region: "",
    package: "",
    currency: "",
  });
  
  const [analytics, setAnalytics] = useState({
    revenue: 0,
    customers: 0,
    payslipsGenerated: 0,
    activeEmployees: 0,
  });
  
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [currencies] = useState(["USD", "EUR", "GBP", "INR", "ZAR"]);
  const [loading, setLoading] = useState(false);
  
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const plansResult = await planService.list();
      const plans = Array.isArray(plansResult) ? plansResult : plansResult?.data || [];
      setPackages(plans);
      
      setCountries(["India", "USA", "UK", "South Africa"]);
      setRegions(["Delhi", "Mumbai", "New York", "London", "Cape Town"]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }
  };

  const fetchAnalytics = async () => {
    if (!Object.values(filters).some(value => value)) {
      return;
    }
    
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    
    try {
      const result = await subscriptionService.stats(filters);
      
      if (result?.status === "success" && result?.data) {
        setAnalytics({
          revenue: result.data.revenue || 0,
          customers: result.data.customers || 0,
          payslipsGenerated: result.data.payslipsGenerated || 0,
          activeEmployees: result.data.activeEmployees || 0,
        });
      } else {
        showAlert("Failed to fetch analytics data", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Error fetching analytics", "error");
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      country: "",
      region: "",
      package: "",
      currency: "",
    });
    setAnalytics({
      revenue: 0,
      customers: 0,
      payslipsGenerated: 0,
      activeEmployees: 0,
    });
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Regional Analytics Dashboard
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Options
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.country}
                  onChange={(e) => handleFilterChange("country", e.target.value)}
                  label="Country"
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  value={filters.region}
                  onChange={(e) => handleFilterChange("region", e.target.value)}
                  label="Region"
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Package</InputLabel>
                <Select
                  value={filters.package}
                  onChange={(e) => handleFilterChange("package", e.target.value)}
                  label="Package"
                >
                  <MenuItem value="">All Packages</MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                      {pkg.planName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={filters.currency}
                  onChange={(e) => handleFilterChange("currency", e.target.value)}
                  label="Currency"
                >
                  <MenuItem value="">All Currencies</MenuItem>
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              size="small"
            >
              Clear All Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4" component="div">
                {formatCurrency(analytics.revenue, filters.currency)}
              </Typography>
              <Typography variant="body2">
                Total amount of successful payments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Customers
              </Typography>
              <Typography variant="h4" component="div">
                {analytics.customers.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Active subscriptions matching filters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Payslips Generated
              </Typography>
              <Typography variant="h4" component="div">
                {analytics.payslipsGenerated.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Payslips created by filtered companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Employees
              </Typography>
              <Typography variant="h4" component="div">
                {analytics.activeEmployees.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Total employees in filtered companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RegionalAnalytics;